import {
  computed,
  defineComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  Teleport,
} from "vue";
import { emitInput, fieldClass, optionLabel, optionValue } from "./shared";

const dropdownOpenEvent = "ui-dropdown-open";
let dropdownInstanceId = 0;

export const UiButton = defineComponent({
  name: "Button",
  inheritAttrs: false,
  props: {
    label: String,
    icon: String,
    iconPos: { type: String, default: "left" },
    severity: String,
    outlined: Boolean,
    disabled: Boolean,
    loading: Boolean,
    type: { type: String, default: "button" },
  },
  setup(props, { attrs, slots, emit }) {
    return () => {
      const danger = props.severity === "danger";
      const secondary = props.severity === "secondary";
      const outlined = props.outlined;
      const classes = [
        "inline-flex min-h-[40px] items-center justify-center gap-2 whitespace-nowrap !rounded-lg px-4 py-2 text-[15px] font-bold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60",
        danger
          ? "border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 focus:ring-rose-200"
          : secondary
          ? "bg-slate-700 text-white hover:bg-slate-800 focus:ring-slate-300"
          : outlined
          ? "border border-teal-600 bg-white text-teal-700 hover:bg-teal-50 focus:ring-teal-200"
          : "bg-teal-600 text-white hover:bg-teal-700 focus:ring-teal-300",
        attrs.class,
      ];
      return h(
        "button",
        {
          ...attrs,
          type: props.type,
          disabled: props.loading || props.disabled || attrs.disabled,
          class: classes,
          "aria-busy": props.loading ? "true" : undefined,
          onClick: (event: MouseEvent) => {
            if (!props.loading) emit("click", event);
          },
        },
        [
          props.loading
            ? h("i", { class: "pi pi-spin pi-spinner" })
            : null,
          props.icon && props.iconPos !== "right" && !props.loading
            ? h("i", { class: props.icon })
            : null,
          props.label
            ? h("span", { class: "whitespace-nowrap" }, props.label)
            : slots.default?.(),
          props.icon && props.iconPos === "right" && !props.loading
            ? h("i", { class: props.icon })
            : null,
        ],
      );
    };
  },
});

export const UiInputText = defineComponent({
  name: "InputText",
  inheritAttrs: false,
  props: { modelValue: [String, Number] },
  setup(props, ctx) {
    return () =>
      h("input", {
        ...ctx.attrs,
        class: [fieldClass, ctx.attrs.class],
        value: props.modelValue ?? "",
        onInput: (event: Event) =>
          emitInput(ctx, (event.target as HTMLInputElement).value),
      });
  },
});

export const UiTextarea = defineComponent({
  name: "Textarea",
  inheritAttrs: false,
  props: { modelValue: String },
  setup(props, ctx) {
    return () =>
      h("textarea", {
        ...ctx.attrs,
        class: [fieldClass, "min-h-[100px] resize-y", ctx.attrs.class],
        value: props.modelValue ?? "",
        onInput: (event: Event) =>
          emitInput(ctx, (event.target as HTMLTextAreaElement).value),
      });
  },
});

export const UiPassword = defineComponent({
  name: "Password",
  inheritAttrs: false,
  props: { modelValue: String, toggleMask: Boolean },
  setup(props, ctx) {
    const visible = ref(false);
    return () =>
      h("div", { class: "relative w-full" }, [
        h("input", {
          ...ctx.attrs,
          class: [
            fieldClass,
            props.toggleMask ? "pr-10" : "",
            ctx.attrs.class,
          ],
          type: visible.value ? "text" : "password",
          value: props.modelValue ?? "",
          onInput: (event: Event) =>
            emitInput(ctx, (event.target as HTMLInputElement).value),
        }),
        props.toggleMask
          ? h(
              "button",
              {
                type: "button",
                class:
                  "absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 transition",
                onClick: () => (visible.value = !visible.value),
              },
              h("i", {
                class: visible.value ? "pi pi-eye" : "pi pi-eye-slash",
              }),
            )
          : null,
      ]);
  },
});

export const UiInputNumber = defineComponent({
  name: "InputNumber",
  inheritAttrs: false,
  props: { modelValue: [String, Number] },
  setup(props, ctx) {
    return () =>
      h("input", {
        ...ctx.attrs,
        class: [fieldClass, ctx.attrs.class],
        type: ctx.attrs.type || "number",
        value: props.modelValue ?? "",
        onInput: (event: Event) =>
          emitInput(ctx, (event.target as HTMLInputElement).value),
      });
  },
});
export const UiInputMask = defineComponent({
  name: "InputMask",
  inheritAttrs: false,
  props: {
    modelValue: [String, Number],
    mask: String,
  },
  setup(props, ctx) {
    const applyMask = (rawValue: string) => {
      if (!props.mask) return rawValue;

      const digits = rawValue.replace(/\D/g, "");
      let digitIndex = 0;
      let masked = "";

      for (const char of props.mask) {
        if (char === "9") {
          if (digitIndex >= digits.length) break;
          masked += digits[digitIndex];
          digitIndex += 1;
        } else if (digitIndex < digits.length) {
          masked += char;
        }
      }

      return masked;
    };

    return () =>
      h("input", {
        ...ctx.attrs,
        class: [fieldClass, ctx.attrs.class],
        type: "text",
        inputmode: "numeric",
        value: props.modelValue ?? "",
        onInput: (event: Event) => {
          const input = event.target as HTMLInputElement;
          const nextValue = applyMask(input.value);
          input.value = nextValue;
          emitInput(ctx, nextValue);
        },
      });
  },
});

export const UiDropdown = defineComponent({
  name: "Dropdown",
  inheritAttrs: false,
  props: {
    modelValue: [String, Number, Object, Boolean],
    options: { type: Array, default: () => [] },
    optionLabel: String,
    optionValue: String,
    placeholder: String,
    showClear: Boolean,
  },
  setup(props, ctx) {
    const open = ref(false);
    const query = ref("");
    const root = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const placement = ref<"top" | "bottom">("bottom");
    const menuMaxHeight = ref(288);
    const menuPosition = ref({ top: 0, left: 0, width: 0 });
    const instanceId = ++dropdownInstanceId;

    const selectedOption = computed(() => {
      if (props.modelValue == null || props.modelValue === "") return null;
      return (
        (props.options as any[]).find((item) => {
          const value = props.optionValue
            ? optionValue(item, props.optionValue)
            : item;
          return props.optionValue
            ? String(value) === String(props.modelValue)
            : item === props.modelValue;
        }) || null
      );
    });

    const selectedLabel = computed(() =>
      selectedOption.value
        ? optionLabel(selectedOption.value, props.optionLabel)
        : props.placeholder || "Chọn",
    );

    const filteredOptions = computed(() => {
      const keyword = query.value.trim().toLowerCase();
      if (!keyword) return props.options as any[];
      return (props.options as any[]).filter((item) =>
        optionLabel(item, props.optionLabel).toLowerCase().includes(keyword),
      );
    });

    const choose = (item: any | null) => {
      const nextValue =
        item == null
          ? null
          : props.optionValue
          ? optionValue(item, props.optionValue)
          : item;
      ctx.emit("update:modelValue", nextValue);
      ctx.emit("change", { value: nextValue });
      open.value = false;
      query.value = "";
    };

    const updatePlacement = () => {
      const rect = root.value?.getBoundingClientRect();
      if (!rect) return;

      const gap = 6;
      const preferredHeight = 288;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const shouldOpenUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
      const available = shouldOpenUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(160, Math.min(preferredHeight, available - 8));

      placement.value = shouldOpenUp ? "top" : "bottom";
      menuMaxHeight.value = maxHeight;
      
      // Calculate position for teleported menu
      menuPosition.value = {
        top: shouldOpenUp ? rect.top - gap : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
      };
    };

    const toggleOpen = () => {
      open.value = !open.value;
      if (open.value) {
        window.dispatchEvent(
          new CustomEvent(dropdownOpenEvent, { detail: instanceId }),
        );
        updatePlacement();
      }
      if (
        open.value &&
        (ctx.attrs.filter === true || ctx.attrs.filter === "true")
      ) {
        nextTick(() => searchInput.value?.focus());
      }
    };

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (root.value && !root.value.contains(event.target as Node)) {
        open.value = false;
        query.value = "";
      }
    };

    const updateOpenPlacement = () => {
      if (open.value) updatePlacement();
    };

    const closeOnAnotherDropdownOpen = (event: Event) => {
      if ((event as CustomEvent<number>).detail !== instanceId) {
        open.value = false;
        query.value = "";
      }
    };

    onMounted(() => {
      document.addEventListener("click", closeOnOutsideClick);
      window.addEventListener(dropdownOpenEvent, closeOnAnotherDropdownOpen);
      window.addEventListener("resize", updateOpenPlacement);
      window.addEventListener("scroll", updateOpenPlacement, true);
    });
    onBeforeUnmount(() => {
      document.removeEventListener("click", closeOnOutsideClick);
      window.removeEventListener(dropdownOpenEvent, closeOnAnotherDropdownOpen);
      window.removeEventListener("resize", updateOpenPlacement);
      window.removeEventListener("scroll", updateOpenPlacement, true);
    });

    return () => [
      h("div", { ref: root, class: ["relative w-full", ctx.attrs.class] }, [
        h(
          "button",
          {
            id: ctx.attrs.id,
            type: "button",
            style: ctx.attrs.style as any,
            class: [
              fieldClass,
              "flex items-center justify-between gap-3 text-left",
              open.value ? "border-teal-600 ring-2 ring-teal-100" : "",
            ],
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              toggleOpen();
            },
          },
          [
            h(
              "span",
              {
                class: [
                  "block truncate",
                  selectedOption.value ? "text-slate-900" : "text-slate-500",
                ],
              },
              selectedLabel.value,
            ),
            h("span", { class: "flex items-center gap-2" }, [
              props.showClear && selectedOption.value
                ? h("i", {
                    class:
                      "pi pi-times rounded-full p-1 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-700",
                    onClick: (event: MouseEvent) => {
                      event.stopPropagation();
                      choose(null);
                    },
                  })
                : null,
              h("i", {
                class: [
                  "pi pi-chevron-down text-xs text-slate-500 transition",
                  open.value ? "rotate-180" : "",
                ],
              }),
            ]),
          ],
        ),
      ]),
      open.value
        ? h(
            Teleport,
            { to: "body" },
            h(
              "div",
              {
                class: "fixed z-[9999] overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-xl",
                style: {
                  top: `${menuPosition.value.top}px`,
                  left: `${menuPosition.value.left}px`,
                  width: `${menuPosition.value.width}px`,
                  maxHeight: `${menuMaxHeight.value}px`,
                  transform:
                    placement.value === "top" ? "translateY(-100%)" : undefined,
                },
              },
              [
                ctx.attrs.filter === true || ctx.attrs.filter === "true"
                  ? h("div", { class: "p-2" }, [
                      h("div", { class: "relative" }, [
                        h("i", {
                          class:
                            "pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400",
                        }),
                        h("input", {
                          ref: searchInput,
                          class: [
                            fieldClass,
                            "min-h-[36px] w-full !pl-8 !pr-3",
                          ],
                          value: query.value,
                          placeholder: "Tìm kiếm",
                          onClick: (event: MouseEvent) =>
                            event.stopPropagation(),
                          onInput: (event: Event) =>
                            (query.value = (
                              event.target as HTMLInputElement
                            ).value),
                        }),
                      ]),
                    ])
                  : null,
                h(
                  "div",
                  { class: "overflow-auto", style: { maxHeight: `${menuMaxHeight.value - 56}px` } },
                  filteredOptions.value.length
                    ? filteredOptions.value.map((item) =>
                        h(
                          "button",
                          {
                            type: "button",
                            class: [
                              "flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-[15px] transition hover:bg-teal-50",
                              item === selectedOption.value
                                ? "bg-teal-50 text-teal-900"
                                : "text-slate-700",
                            ],
                            onClick: (event: MouseEvent) => {
                              event.stopPropagation();
                              choose(item);
                            },
                          },
                          [
                            h(
                              "span",
                              { class: "min-w-0 flex-1 truncate" },
                              ctx.slots.option
                                ? ctx.slots.option({ option: item })
                                : optionLabel(item, props.optionLabel),
                            ),
                            item === selectedOption.value
                              ? h("i", {
                                  class: "pi pi-check text-xs text-teal-700",
                                })
                              : null,
                          ],
                        ),
                      )
                    : h(
                        "div",
                        { class: "px-3 py-2 text-[15px] text-slate-500" },
                        "Không có dữ liệu",
                      ),
                ),
              ],
            ),
          )
        : null,
    ];
  },
});

export const UiMultiSelect = defineComponent({
  name: "MultiSelect",
  inheritAttrs: false,
  props: {
    modelValue: { type: Array, default: () => [] },
    options: { type: Array, default: () => [] },
    optionLabel: String,
    optionValue: String,
    placeholder: String,
  },
  setup(props, ctx) {
    const open = ref(false);
    const query = ref("");
    const root = ref<HTMLElement | null>(null);
    const searchInput = ref<HTMLInputElement | null>(null);
    const placement = ref<"top" | "bottom">("bottom");
    const menuMaxHeight = ref(288);
    const menuPosition = ref({ top: 0, left: 0, width: 0 });
    const instanceId = ++dropdownInstanceId;

    const isSelected = (option: any) => {
      const value = props.optionValue
        ? optionValue(option, props.optionValue)
        : option;
      return (props.modelValue as any[]).some((item) =>
        props.optionValue ? String(item) === String(value) : item === option,
      );
    };

    const selectedLabel = computed(() => {
      const selected = (props.options as any[]).filter(isSelected);
      if (!selected.length) return props.placeholder || "Chọn";
      const labels = selected
        .slice(0, 2)
        .map((item) => optionLabel(item, props.optionLabel));
      return selected.length > 2
        ? `${labels.join(", ")} +${selected.length - 2}`
        : labels.join(", ");
    });

    const filteredOptions = computed(() => {
      const keyword = query.value.trim().toLowerCase();
      if (!keyword) return props.options as any[];
      return (props.options as any[]).filter((item) =>
        optionLabel(item, props.optionLabel).toLowerCase().includes(keyword),
      );
    });

    const toggle = (option: any) => {
      const value = props.optionValue
        ? optionValue(option, props.optionValue)
        : option;
      const current = [...(props.modelValue as any[])];
      const index = current.findIndex((item) =>
        props.optionValue ? String(item) === String(value) : item === option,
      );
      const next =
        index >= 0
          ? current.filter((_, itemIndex) => itemIndex !== index)
          : [...current, value];
      ctx.emit("update:modelValue", next);
      ctx.emit("change", { value: next });
    };

    const updatePlacement = () => {
      const rect = root.value?.getBoundingClientRect();
      if (!rect) return;

      const gap = 6;
      const preferredHeight = 288;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const shouldOpenUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
      const available = shouldOpenUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(160, Math.min(preferredHeight, available - 8));

      placement.value = shouldOpenUp ? "top" : "bottom";
      menuMaxHeight.value = maxHeight;
      
      // Calculate position for teleported menu
      menuPosition.value = {
        top: shouldOpenUp ? rect.top - gap : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
      };
    };

    const toggleOpen = () => {
      open.value = !open.value;
      if (open.value) {
        window.dispatchEvent(
          new CustomEvent(dropdownOpenEvent, { detail: instanceId }),
        );
        updatePlacement();
      }
      if (
        open.value &&
        (ctx.attrs.filter === true || ctx.attrs.filter === "true")
      ) {
        nextTick(() => searchInput.value?.focus());
      }
    };

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (root.value && !root.value.contains(event.target as Node)) {
        open.value = false;
        query.value = "";
      }
    };

    const updateOpenPlacement = () => {
      if (open.value) updatePlacement();
    };

    const closeOnAnotherDropdownOpen = (event: Event) => {
      if ((event as CustomEvent<number>).detail !== instanceId) {
        open.value = false;
        query.value = "";
      }
    };

    onMounted(() => {
      document.addEventListener("click", closeOnOutsideClick);
      window.addEventListener(dropdownOpenEvent, closeOnAnotherDropdownOpen);
      window.addEventListener("resize", updateOpenPlacement);
      window.addEventListener("scroll", updateOpenPlacement, true);
    });
    onBeforeUnmount(() => {
      document.removeEventListener("click", closeOnOutsideClick);
      window.removeEventListener(dropdownOpenEvent, closeOnAnotherDropdownOpen);
      window.removeEventListener("resize", updateOpenPlacement);
      window.removeEventListener("scroll", updateOpenPlacement, true);
    });

    return () => [
      h("div", { ref: root, class: ["relative w-full", ctx.attrs.class] }, [
        h(
          "button",
          {
            id: ctx.attrs.id,
            type: "button",
            style: ctx.attrs.style as any,
            class: [
              fieldClass,
              "flex items-center justify-between gap-3 text-left",
              open.value ? "border-teal-600 ring-2 ring-teal-100" : "",
            ],
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              toggleOpen();
            },
          },
          [
            h(
              "span",
              {
                class: [
                  "block truncate",
                  (props.modelValue as any[]).length
                    ? "text-slate-900"
                    : "text-slate-500",
                ],
              },
              selectedLabel.value,
            ),
            h("i", {
              class: [
                "pi pi-chevron-down text-xs text-slate-500 transition",
                open.value ? "rotate-180" : "",
              ],
            }),
          ],
        ),
      ]),
      open.value
        ? h(
            Teleport,
            { to: "body" },
            h(
              "div",
              {
                class: "fixed z-[9999] overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-xl",
                style: {
                  top: `${menuPosition.value.top}px`,
                  left: `${menuPosition.value.left}px`,
                  width: `${menuPosition.value.width}px`,
                  maxHeight: `${menuMaxHeight.value}px`,
                  transform:
                    placement.value === "top" ? "translateY(-100%)" : undefined,
                },
              },
              [
                ctx.attrs.filter === true || ctx.attrs.filter === "true"
                  ? h("div", { class: "p-2" }, [
                      h("div", { class: "relative" }, [
                        h("i", {
                          class:
                            "pi pi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400",
                        }),
                        h("input", {
                          ref: searchInput,
                          class: [
                            fieldClass,
                            "min-h-[36px] w-full !pl-8 !pr-3",
                          ],
                          value: query.value,
                          placeholder: "Tìm kiếm",
                          onClick: (event: MouseEvent) =>
                            event.stopPropagation(),
                          onInput: (event: Event) =>
                            (query.value = (
                              event.target as HTMLInputElement
                            ).value),
                        }),
                      ]),
                    ])
                  : null,
                h(
                  "div",
                  { class: "overflow-auto", style: { maxHeight: `${menuMaxHeight.value - 56}px` } },
                  filteredOptions.value.length
                    ? filteredOptions.value.map((item) =>
                        h(
                          "button",
                          {
                            type: "button",
                            class: [
                              "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-[15px] transition hover:bg-teal-50",
                              isSelected(item)
                                ? "bg-teal-50 text-teal-900"
                                : "text-slate-700",
                            ],
                            onClick: (event: MouseEvent) => {
                              event.stopPropagation();
                              toggle(item);
                            },
                          },
                          [
                            h(
                              "span",
                              {
                                class: [
                                  "flex h-4 w-4 items-center justify-center rounded border text-[10px]",
                                  isSelected(item)
                                    ? "border-teal-600 bg-teal-600 text-white"
                                    : "border-slate-300 bg-white",
                                ],
                              },
                              isSelected(item) ? "✓" : "",
                            ),
                            h(
                              "span",
                              { class: "min-w-0 flex-1 truncate" },
                              optionLabel(item, props.optionLabel),
                            ),
                          ],
                        ),
                      )
                    : h(
                        "div",
                        { class: "px-3 py-2 text-[15px] text-slate-500" },
                        "Không có dữ liệu",
                      ),
                ),
              ],
            ),
          )
        : null,
    ];
  },
});

export const UiCheckbox = defineComponent({
  name: "Checkbox",
  inheritAttrs: false,
  props: {
    modelValue: [Boolean, Array],
    binary: Boolean,
    value: [String, Number, Boolean],
  },
  setup(props, ctx) {
    return () =>
      h("input", {
        ...ctx.attrs,
        type: "checkbox",
        class: [
          "h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500",
          ctx.attrs.class,
        ],
        checked: !!props.modelValue,
        onChange: (event: Event) =>
          ctx.emit(
            "update:modelValue",
            (event.target as HTMLInputElement).checked,
          ),
      });
  },
});

export const UiRadioButton = defineComponent({
  name: "RadioButton",
  inheritAttrs: false,
  props: {
    modelValue: [String, Number, Boolean],
    value: [String, Number, Boolean],
  },
  setup(props, ctx) {
    return () =>
      h("input", {
        ...ctx.attrs,
        type: "radio",
        class: [
          "h-4 w-4 border-slate-300 text-teal-600 focus:ring-teal-500",
          ctx.attrs.class,
        ],
        checked: props.modelValue === props.value,
        value: props.value as any,
        onChange: () => ctx.emit("update:modelValue", props.value),
      });
  },
});

export const UiCalendar = defineComponent({
  name: "Calendar",
  inheritAttrs: false,
  props: {
    modelValue: [String, Number, Date],
    view: String,
    placeholder: String,
    showTime: Boolean,
  },
  setup(props, ctx) {
    const root = ref<HTMLElement | null>(null);
    const open = ref(false);
    const cursor = ref(new Date());
    const timeValue = ref("08:00");
    const weekdays = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
    const monthNames = [
      "Tháng 1",
      "Tháng 2",
      "Tháng 3",
      "Tháng 4",
      "Tháng 5",
      "Tháng 6",
      "Tháng 7",
      "Tháng 8",
      "Tháng 9",
      "Tháng 10",
      "Tháng 11",
      "Tháng 12",
    ];

    const pad = (value: number) => String(value).padStart(2, "0");
    const parseDate = (value: any) => {
      if (!value) return null;
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return new Date(value);
      }
      if (typeof value === "number") {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
      }
      const match = String(value).match(
        /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/,
      );
      if (match) {
        return new Date(
          Number(match[1]),
          Number(match[2]) - 1,
          Number(match[3]),
          Number(match[4] ?? 0),
          Number(match[5] ?? 0),
        );
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    };
    const formatDate = (date: Date) =>
      `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    const formatDateTime = (date: Date) =>
      `${formatDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    const displayValue = computed(() => {
      const date = parseDate(props.modelValue);
      if (!date) return "";
      if (props.view === "year") return String(date.getFullYear());
      const dateText = `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
      return props.showTime
        ? `${pad(date.getHours())}:${pad(date.getMinutes())} ${dateText}`
        : dateText;
    });
    const days = computed(() => {
      const first = new Date(cursor.value.getFullYear(), cursor.value.getMonth(), 1);
      const start = new Date(first);
      start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
      return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return date;
      });
    });

    const syncCursor = () => {
      const date = parseDate(props.modelValue) ?? new Date();
      cursor.value = new Date(date);
      timeValue.value = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };
    const emitValue = (date: Date | null) => {
      if (!date) {
        ctx.emit("update:modelValue", "");
        ctx.emit("change", { value: "" });
        return;
      }
      const [hour, minute] = timeValue.value.split(":").map(Number);
      date.setHours(hour || 0, minute || 0, 0, 0);
      const next =
        props.modelValue instanceof Date
          ? date
          : props.showTime
          ? formatDateTime(date)
          : formatDate(date);
      ctx.emit("update:modelValue", next);
      ctx.emit("change", { value: next });
    };
    const chooseDate = (date: Date) => {
      emitValue(new Date(date));
      if (!props.showTime) open.value = false;
    };
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (root.value && !root.value.contains(event.target as Node)) {
        open.value = false;
      }
    };

    onMounted(() => document.addEventListener("click", closeOnOutsideClick));
    onBeforeUnmount(() =>
      document.removeEventListener("click", closeOnOutsideClick),
    );

    const yearValue = computed(() => {
      const date = parseDate(props.modelValue);
      return date ? String(date.getFullYear()) : "";
    });
    return () => {
      const { class: attrClass, ...attrs } = ctx.attrs;
      if (props.view === "year") {
        return h("input", {
          ...attrs,
          class: [fieldClass, attrClass],
          type: "number",
          placeholder: props.placeholder || "Chọn năm",
          value: yearValue.value,
          onInput: (event: Event) => {
            const year = Number((event.target as HTMLInputElement).value);
            if (!year) return ctx.emit("update:modelValue", "");
            ctx.emit("update:modelValue", new Date(year, 0, 1));
          },
        });
      }

      const selected = parseDate(props.modelValue);
      return h("div", { ref: root, class: ["relative w-full", attrClass] }, [
        h(
          "button",
          {
            ...attrs,
            type: "button",
            class: [
              fieldClass,
              "flex w-full items-center justify-between text-left",
            ],
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              syncCursor();
              open.value = !open.value;
            },
          },
          [
            h(
              "span",
              {
                class: displayValue.value
                  ? "text-slate-900"
                  : "text-slate-400",
              },
              displayValue.value || props.placeholder || "dd/mm/yyyy",
            ),
            h("i", { class: "pi pi-calendar text-slate-500 text-sm" }),
          ],
        ),
        open.value
          ? h(
              "div",
              {
                class:
                  "absolute left-0 top-[calc(100%+8px)] z-[1600] w-[330px] rounded-xl border border-slate-200 bg-white p-4 shadow-2xl",
                onClick: (event: MouseEvent) => event.stopPropagation(),
              },
              [
                h("div", { class: "mb-4 flex items-center justify-between" }, [
                  h(
                    "button",
                    {
                      type: "button",
                      class:
                        "rounded-lg px-3 py-2 text-xl font-bold text-slate-700 hover:bg-slate-100",
                      onClick: () =>
                        (cursor.value = new Date(
                          cursor.value.getFullYear(),
                          cursor.value.getMonth() - 1,
                          1,
                        )),
                    },
                    "‹",
                  ),
                  h(
                    "div",
                    { class: "text-base font-bold text-slate-800" },
                    `${monthNames[cursor.value.getMonth()]} ${cursor.value.getFullYear()}`,
                  ),
                  h(
                    "button",
                    {
                      type: "button",
                      class:
                        "rounded-lg px-3 py-2 text-xl font-bold text-slate-700 hover:bg-slate-100",
                      onClick: () =>
                        (cursor.value = new Date(
                          cursor.value.getFullYear(),
                          cursor.value.getMonth() + 1,
                          1,
                        )),
                    },
                    "›",
                  ),
                ]),
                h(
                  "div",
                  { class: "grid grid-cols-7 gap-1 text-center text-sm font-semibold text-slate-500" },
                  weekdays.map((day) => h("span", { class: "py-1" }, day)),
                ),
                h(
                  "div",
                  { class: "mt-1 grid grid-cols-7 gap-1" },
                  days.value.map((date) => {
                    const isCurrentMonth =
                      date.getMonth() === cursor.value.getMonth();
                    const isSelected =
                      selected &&
                      date.getFullYear() === selected.getFullYear() &&
                      date.getMonth() === selected.getMonth() &&
                      date.getDate() === selected.getDate();
                    const today = new Date();
                    const isToday =
                      date.getFullYear() === today.getFullYear() &&
                      date.getMonth() === today.getMonth() &&
                      date.getDate() === today.getDate();
                    return h(
                      "button",
                      {
                        type: "button",
                        class: [
                          "h-9 rounded-lg text-sm font-semibold transition",
                          isSelected
                            ? "bg-teal-100 text-teal-700 ring-1 ring-teal-500"
                            : isToday
                            ? "bg-slate-50 text-teal-700 ring-1 ring-teal-200"
                            : isCurrentMonth
                            ? "text-slate-700 hover:bg-teal-50 hover:text-teal-700"
                            : "text-slate-300 hover:bg-slate-50",
                        ],
                        onClick: () => chooseDate(date),
                      },
                      String(date.getDate()),
                    );
                  }),
                ),
                props.showTime
                  ? h("div", { class: "mt-4 flex items-center gap-3" }, [
                      h("label", { class: "text-sm font-semibold text-slate-600" }, "Giờ"),
                      h("input", {
                        class:
                          "h-10 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-700 outline-none focus:border-teal-300 focus:ring-2 focus:ring-teal-50",
                        type: "time",
                        value: timeValue.value,
                        onInput: (event: Event) => {
                          timeValue.value = (event.target as HTMLInputElement).value;
                          if (selected) emitValue(new Date(selected));
                        },
                      }),
                    ])
                  : null,
                h("div", { class: "mt-4 flex justify-between border-t border-slate-100 pt-3" }, [
                  h(
                    "button",
                    {
                      type: "button",
                        class:
                          "rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100",
                      onClick: () => emitValue(null),
                    },
                    "Xóa",
                  ),
                  h("div", { class: "flex gap-2" }, [
                    h(
                      "button",
                      {
                        type: "button",
                        class:
                          "rounded-lg px-3 py-2 text-sm font-semibold text-teal-700 hover:bg-teal-50",
                        onClick: () => chooseDate(new Date()),
                      },
                      "Hôm nay",
                    ),
                    props.showTime
                      ? h(
                          "button",
                          {
                            type: "button",
                            class:
                              "rounded-lg bg-teal-50 px-3 py-2 text-sm font-semibold text-teal-700 ring-1 ring-teal-200 hover:bg-teal-100",
                            onClick: () => (open.value = false),
                          },
                          "Xong",
                        )
                      : null,
                  ]),
                ]),
              ],
            )
          : null,
      ]);
    };
  },
});

export const UiSlider = defineComponent({
  name: "Slider",
  inheritAttrs: false,
  props: { modelValue: [Number, Array], range: Boolean },
  setup(props, ctx) {
    const minValue = () =>
      Array.isArray(props.modelValue)
        ? Number(props.modelValue[0] ?? 0)
        : Number(props.modelValue ?? 0);
    const maxValue = () =>
      Array.isArray(props.modelValue)
        ? Number(props.modelValue[1] ?? 100)
        : 100;

    const emitRange = (nextMin: number, nextMax: number) => {
      const min = Math.min(nextMin, nextMax);
      const max = Math.max(nextMin, nextMax);
      ctx.emit("update:modelValue", [min, max]);
      ctx.emit("change", { value: [min, max] });
    };

    return () => {
      if (!props.range) {
        return h("input", {
          ...ctx.attrs,
          type: "range",
          min: 0,
          max: 100,
          class: ["w-full accent-teal-600", ctx.attrs.class],
          value: minValue(),
          onInput: (event: Event) => {
            const value = Number((event.target as HTMLInputElement).value);
            ctx.emit("update:modelValue", value);
            ctx.emit("change", { value });
          },
        });
      }

      const start = Math.min(minValue(), maxValue());
      const end = Math.max(minValue(), maxValue());

      return h("div", { class: ["relative h-10 w-full", ctx.attrs.class] }, [
        h("div", {
          class:
            "absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-slate-200",
        }),
        h("div", {
          class:
            "absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-teal-600",
          style: { left: `${start}%`, right: `${100 - end}%` },
        }),
        h("input", {
          type: "range",
          min: 0,
          max: 100,
          value: start,
          class:
            "pointer-events-none absolute inset-x-0 top-1/2 h-1 w-full -translate-y-1/2 appearance-none bg-transparent accent-teal-600 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-teal-600 [&::-webkit-slider-thumb]:shadow-md",
          onInput: (event: Event) =>
            emitRange(Number((event.target as HTMLInputElement).value), end),
        }),
        h("input", {
          type: "range",
          min: 0,
          max: 100,
          value: end,
          class:
            "pointer-events-none absolute inset-x-0 top-1/2 h-1 w-full -translate-y-1/2 appearance-none bg-transparent accent-teal-600 [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-teal-600 [&::-webkit-slider-thumb]:shadow-md",
          onInput: (event: Event) =>
            emitRange(start, Number((event.target as HTMLInputElement).value)),
        }),
      ]);
    };
  },
});
